import type { FC } from 'react';
import { createContext, useContext, useMemo } from 'react';
import { gql } from '@apollo/client';

import type {
  OrderHistoryDetailPaymentQueryResult,
  OrderHistoryDetailQueryResult,
  OrderItemFragment,
  PaymentDataFragment,
  StoreConfigDataFragment,
} from './__graphql__/OrderHistoryDetailContext';
import {
  useOrderHistoryDetailPaymentQuery,
  useOrderHistoryDetailQuery,
} from './__graphql__/OrderHistoryDetailContext';

interface OrderHistoryDetailContextValue {
  storeConfig: StoreConfigDataFragment | null;
  itemsData: OrderItemFragment | null;
  paymentData: PaymentDataFragment | null;
  itemsQueryState: Omit<OrderHistoryDetailQueryResult, 'data'>;
  paymentQueryState: Omit<OrderHistoryDetailPaymentQueryResult, 'data'>;
  hasHardware: boolean;
  contactEmail: string | null;
}

const initialValue: OrderHistoryDetailContextValue = {
  storeConfig: null,
  itemsData: null,
  paymentData: null,
  itemsQueryState: {
    loading: true,
  } as OrderHistoryDetailContextValue['itemsQueryState'],
  paymentQueryState: {
    loading: true,
  } as OrderHistoryDetailContextValue['paymentQueryState'],
  hasHardware: false,
  contactEmail: null,
};

const OrderHistoryDetailContext =
  createContext<OrderHistoryDetailContextValue>(initialValue);
OrderHistoryDetailContext.displayName = 'OrderHistoryDetailContext';

export const OrderHistoryDetailProvider: FC<{
  orderNumber: string;
  children: React.ReactNode;
}> = ({ orderNumber, children }) => {
  const { data: itemsData, ...itemsQueryState } = useOrderHistoryDetailQuery({
    skip: !orderNumber,
    ssr: false,
    variables: { orderNumber: orderNumber || '' },
  });
  const { data: addressesData, ...paymentQueryState } =
    useOrderHistoryDetailPaymentQuery({
      skip: !orderNumber,
      variables: { orderNumber: orderNumber || '' },
    });

  const value = useMemo<OrderHistoryDetailContextValue>(() => {
    const orderItems =
      itemsData?.magento2?.customer?.globalOrders?.items?.[0]?.items;

    return {
      itemsQueryState,
      paymentQueryState,
      contactEmail: itemsData?.magento2?.customer?.email ?? null,
      storeConfig: itemsData?.magento2?.storeConfig ?? null,
      itemsData:
        itemsData?.magento2?.customer?.globalOrders?.items?.[0] ?? null,
      paymentData:
        addressesData?.magento2?.customer?.globalOrders?.items?.[0] ?? null,
      hasHardware: orderItems?.some((item) => !isSoftwareItem(item)) ?? false,
    };
  }, [addressesData, paymentQueryState, itemsData, itemsQueryState]);

  return (
    <OrderHistoryDetailContext.Provider value={value}>
      <>{children}</>
    </OrderHistoryDetailContext.Provider>
  );
};

export const useOrderHistoryDetail = (): OrderHistoryDetailContextValue =>
  useContext(OrderHistoryDetailContext);

export const isSoftwareItem = <T extends { productType?: string | null }>(
  item?: T | null,
): boolean => /^virtual$/i.test(item?.productType ?? '');

/*
Splitting into 2 queries, trying to handle in one query blows out max query complexity and we get e.g.
{
  field: ""
  id: "unknown_error"
  message: "Max query complexity should be 300 but got 304."
  path: "magento2"
}
*/

export const ORDER_HISTORY_DETAIL_ITEMS_QUERY = gql`
  fragment OrderItemProduct on Magento2_OrderItem {
    id
    status
    productSku
    productName
    returnStatus
    productSalePrice {
      value
      currency
    }
    productSalePriceIncludingTax {
      currency
      value
    }
    rowTotal {
      currency
      value
    }
    rowTotalIncludingTax {
      currency
      value
    }
    productType
    quantityOrdered
    quantityRefunded
    quantityReturned
    eligibleForReturn
    quantityCanceled
    quantityInvoiced
  }

  fragment OrderItem on Magento2_GlobalCustomerOrder {
    id
    number
    orderDate
    shippingMethod
    shipments {
      tracking {
        number
      }
    }
    total {
      subtotal {
        value
        currency
      }
      subtotalIncludingTax {
        currency
        value
      }
      discounts {
        label
        amount {
          value
          currency
        }
      }
      taxes {
        title
        rate
        amount {
          value
          currency
        }
      }
      totalShipping {
        value
        currency
      }
      shippingHandling {
        amountExcludingTax {
          value
          currency
        }
        amountIncludingTax {
          value
          currency
        }
      }
      grandTotal {
        value
        currency
      }
    }
    status
    statusCode
    items {
      ...OrderItemProduct
    }
  }

  fragment StoreConfigData on Magento2_StoreConfig {
    storeCode
    taxSalesDisplayPrice
    taxSalesDisplaySubtotal
  }

  query OrderHistoryDetail($orderNumber: String) {
    magento2 {
      storeConfig {
        ...StoreConfigData
      }
      customer {
        email
        nativeId
        globalOrders(filter: { number: { eq: $orderNumber } }) {
          items {
            id
            number
            orderDate
            shippingMethod
            shipments {
              tracking {
                number
              }
            }
            total {
              subtotal {
                value
                currency
              }
              subtotalIncludingTax {
                currency
                value
              }
              discounts {
                label
                amount {
                  value
                  currency
                }
              }
              taxes {
                title
                rate
                amount {
                  value
                  currency
                }
              }
              totalShipping {
                value
                currency
              }
              shippingHandling {
                amountExcludingTax {
                  value
                  currency
                }
                amountIncludingTax {
                  value
                  currency
                }
              }
              grandTotal {
                value
                currency
              }
            }
            status
            statusCode
            items {
              id
              status
              productSku
              productName
              returnStatus
              productSalePrice {
                value
                currency
              }
              productSalePriceIncludingTax {
                currency
                value
              }
              rowTotal {
                currency
                value
              }
              rowTotalIncludingTax {
                currency
                value
              }
              productType
              quantityOrdered
              quantityRefunded
              quantityReturned
              eligibleForReturn
              quantityCanceled
              quantityInvoiced
            }
          }
        }
      }
    }
  }
`;

export const ORDER_HISTORY_DETAIL_PAYMENT_QUERY = gql`
  fragment AddressDetails on Magento2_OrderAddress {
    prefix
    firstname
    middlename
    lastname
    suffix
    street
    countryCode
    region
    postcode
    city
    telephone
    company
  }

  fragment PaymentData on Magento2_GlobalCustomerOrder {
    id
    billingAddress {
      ...AddressDetails
    }
    shippingAddress {
      ...AddressDetails
    }
    shippingMethod
    carrier
    paymentMethods {
      name
    }
  }

  query OrderHistoryDetailPayment($orderNumber: String) {
    magento2 {
      customer {
        nativeId
        globalOrders(filter: { number: { eq: $orderNumber } }) {
          items {
            ...PaymentData
          }
        }
      }
    }
  }
`;
